import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isDirty } from "@totalsoft/change-tracking";
import { useChangeTrackingLens, get } from "@totalsoft/change-tracking-react";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import ProfileDataCard from "./ProfileDataCard";
import PersonalDataCard from "./PersonalDataCard";
import CommandsCard from "../../common/components/CommandsCard";
import { UPDATE_USER_PROFILE } from "features/user/mutations";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { addToast } from "components/notifications";
import { USER_PROFILE_QUERY } from "../queries";
import { emptyObject } from "constants/defaultValues";
import { useContentFooter } from "hooks/mainContent";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import ProfileDataCardCIAM from "./ProfileDataCardCIAM";
import { env } from "utils/env";

function MyProfileContainer() {
  const { t } = useTranslation();
  const [myProfileLens, dirtyInfo, resetMyProfileLens] = useChangeTrackingLens(emptyObject);
  const [, setFooter] = useContentFooter();

  const { loading } = useQueryWithErrorHandling(USER_PROFILE_QUERY, {
    onCompleted: (e) => {
      if (!e?.getUserProfile) return;
      resetMyProfileLens({ ...e.getUserProfile });
    },
  });

  const [updateUserProfile, { loading: saving }] = useMutationWithErrorHandling(UPDATE_USER_PROFILE, {
    onCompleted: (e) => {
      addToast(t("ToastNotifications.Success"), "success");
      resetMyProfileLens({ ...e.updateUserProfile });
    },
  });

  const handleSave = useCallback(() => {
    if (!isDirty(dirtyInfo)) return;
    const input = isDirty(dirtyInfo.picture) ? myProfileLens |> get : { ...(myProfileLens |> get), picture: undefined };
    updateUserProfile({ variables: { input } });
  }, [updateUserProfile, myProfileLens, dirtyInfo]);

  useEffect(() => {
    setFooter(
      <CommandsCard
        buttonRight={
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isDirty(dirtyInfo)}
            endIcon={saving ? <CircularProgress size={20} color={"inherit"} /> : <CheckIcon />}
          >
            {t("ProfilePage.Save")}
          </Button>
        }
      />
    );
  }, [t, setFooter, handleSave, saving, dirtyInfo]);

  if (loading || saving) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {env.REACT_APP_USE_CIAM?.toLowerCase() === "true" ? (
          <ProfileDataCardCIAM userProfileLens={myProfileLens} />
        ) : (
          <ProfileDataCard userProfileLens={myProfileLens} />
        )}
      </Grid>
      <Grid item xs={12}>
        <PersonalDataCard userProfileLens={myProfileLens} />
      </Grid>
    </Grid>
  );
}

export default MyProfileContainer;
